// src/data/production.ts
// Production's catalogue and arithmetic for the page (55.55), read from game configuration.
// The server (functions/src/production.ts) is the authority; this mirrors it so the booking form
// can say what a job will yield and how long it takes before the player commits to it.
import { getDomain } from '../config/gameConfig';
import { InventoryItem } from '../types/inventory';
import { Material, ProductionJob, ProductionSettings } from '../types/production';
import { ShopItem } from '../types/shop';
import { getShopItems } from './shop';

export const ACCESS_ADMINS = 2;
export const ACCESS_OPEN = 3;

const DEFAULTS: ProductionSettings = {
    productionAccess: 1,
    productionMaxHours: 12,
    productionXpPerHour: 100,
    productionSpeedPerLevel: 0.01,
    productionSpeedCap: 0.5,
    toolTier2Multiplier: 2,
    toolTier3Multiplier: 4,
    toolTier2SkillLevel: 20,
    toolTier3SkillLevel: 50,
    deviceSkillLevel: 15,
    deviceSellBackShare: 0.4,
    deviceTierBonus: 0.2,
};

interface EconomySetting { id: string; value: number }

/** The production numbers, each falling back as the server's does: missing or not positive means the default. */
export function getProductionSettings(): ProductionSettings {
    const economy = getDomain<EconomySetting>('economy');
    const out = { ...DEFAULTS };
    for (const key of Object.keys(DEFAULTS) as (keyof ProductionSettings)[]) {
        const raw = Number(economy.find((e) => e.id === key)?.value);
        if (Number.isFinite(raw) && raw > 0) out[key] = raw;
    }
    return out;
}

/** Whether production is open to this player: 3 everyone, 2 admins only, 1 nobody. */
export function productionOpenFor(isAdmin: boolean, settings: ProductionSettings = getProductionSettings()): boolean {
    const access = Math.floor(settings.productionAccess);
    return access >= ACCESS_OPEN || (access >= ACCESS_ADMINS && isAdmin);
}

const byOrder = <T extends { order?: number }>(a: T, b: T): number => (a.order ?? 0) - (b.order ?? 0);

/**
 * The three trades, each on its own page since 55.59 (the owner, 11 October), in the order the
 * menu and the dashboard list them. A trade's page is at its skill id: /metsandus and so on.
 */
export const TRADE_SKILLS = ['metsandus', 'kaevandus', 'metallitoo'] as const;
export type TradeSkill = typeof TRADE_SKILLS[number];
export const isTradeSkill = (v: unknown): v is TradeSkill => TRADE_SKILLS.includes(v as TradeSkill);
export const tradePath = (skill: TradeSkill): string => `/${skill}`;

export const getMaterials = (): Material[] => [...getDomain<Material>('materials')].sort(byOrder);

export const materialName = (id: string): string => getMaterials().find((m) => m.id === id)?.name ?? id;

export const getProductionJobs = (): ProductionJob[] => [...getDomain<ProductionJob>('productionJobs')].sort(byOrder);

/** A trade's jobs as the page offers them: hidden ones (55.99) left out. */
export const jobsForSkill = (skill: string): ProductionJob[] => getProductionJobs().filter((j) => j.skill === skill && !j.hidden);

/** The tool slot that sets a job's speed (55.56): shared where two jobs use the same tools. */
export const toolSlot = (job: ProductionJob): string => job.tool || job.id;

export const alsoNeeds = (job: ProductionJob): string[] => job.alsoNeeds ?? [];

/** The jobs a tool in this slot serves — timed by it, or needing it as well. */
export const jobsUsingSlot = (slot: string): ProductionJob[] =>
    getProductionJobs().filter((j) => toolSlot(j) === slot || alsoNeeds(j).includes(slot));

/** Every slot a tool may fill, with the jobs it serves as its label — for the admin panel. */
export const getToolSlots = (): { id: string; label: string }[] => {
    const ids = Array.from(new Set(getProductionJobs().flatMap((j) => [toolSlot(j), ...alsoNeeds(j)])));
    return ids.map((id) => ({ id, label: slotLabel(id) }));
};

/** "Kaevanda kivi / Kaevanda maaki" — what a tool in this slot is for. */
export const slotLabel = (slot: string): string => jobsUsingSlot(slot).map((j) => j.name).join(' / ') || slot;

/** The shop's tools for a slot, hand tool first. */
export const toolsForSlot = (slot: string): ShopItem[] =>
    getShopItems().filter((i) => i.type === 'tool' && i.job === slot).sort((a, b) => a.value - b.value);

/** The trade whose jobs make a material, so a link about a missing material can open its page. */
export function materialTrade(material: string): TradeSkill | null {
    const skill = getProductionJobs().find((j) => j.output.material === material)?.skill;
    return isTradeSkill(skill) ? skill : null;
}

export const isExtraction = (job: ProductionJob): boolean => job.inputs.length === 0;

/** The workshop devices the character owns (55.70); as many as the workshop's level at most. */
export const devicesOwned = (inventory: InventoryItem[] | undefined): InventoryItem[] =>
    (inventory ?? []).filter((i) => i.category === 'tool' && i.toolEffect?.device === true);
/** The best tool tier owned for a slot, 0 for none. */
export function toolTierFor(inventory: InventoryItem[] | undefined, slot: string): number {
    let best = 0;
    for (const item of inventory ?? []) {
        if (item.category !== 'tool' || item.toolEffect?.job !== slot) continue;
        best = Math.max(best, Math.min(3, Math.max(0, Math.floor(Number(item.toolEffect.tier) || 0))));
    }
    return best;
}

export function tierMultiplier(tier: number, s: ProductionSettings = getProductionSettings()): number {
    if (tier >= 3) return s.toolTier3Multiplier;
    if (tier === 2) return s.toolTier2Multiplier;
    return 1;
}

export function tierSkillLevel(tier: number, s: ProductionSettings = getProductionSettings()): number {
    if (tier >= 3) return s.toolTier3SkillLevel;
    if (tier === 2) return s.toolTier2SkillLevel;
    return 0;
}

export function skillSpeed(level: number, s: ProductionSettings = getProductionSettings()): number {
    return 1 + Math.min(s.productionSpeedCap, Math.max(0, level) * s.productionSpeedPerLevel);
}

export function batchesPerHourFor(job: ProductionJob, tier: number, level: number, s: ProductionSettings = getProductionSettings(), factor = 1): number {
    return job.batchesPerHour * tierMultiplier(tier, s) * skillSpeed(level, s) * factor;
}

/** The step (1–3) of the workshop device a job needs, 0 for none (55.72). Mirrors deviceTierFor on the server. */
export function deviceTierFor(inventory: InventoryItem[] | undefined, job: ProductionJob): number {
    let best = 0;
    for (const item of inventory ?? []) {
        if (item.category !== 'tool' || !item.toolEffect?.device || !alsoNeeds(job).includes(item.toolEffect.job)) continue;
        best = Math.max(best, Math.min(3, Math.max(1, Math.floor(Number(item.toolEffect.tier) || 1))));
    }
    return best;
}

/** What a device step adds to its products' speed: 1 for the first, then +`deviceTierBonus` a step. */
export const deviceFactor = (deviceTier: number, s: ProductionSettings = getProductionSettings()): number =>
    1 + Math.max(0, deviceTier - 1) * Math.max(0, s.deviceTierBonus);

export function materialCount(inventory: InventoryItem[] | undefined, material: string): number {
    return (inventory ?? [])
        .filter((i) => i.category === 'material' && i.itemId === material)
        .reduce((sum, i) => sum + Math.max(0, Math.floor(Number(i.quantity) || 0)), 0);
}

/** What a booking would do, by the server's arithmetic. `amount` is hours for extraction, batches otherwise. */
export function planJob(job: ProductionJob, amount: number, tier: number, level: number, s: ProductionSettings = getProductionSettings(), factor = 1):
    { batches: number; minutes: number; output: number; xp: number } {
    const perHour = batchesPerHourFor(job, tier, level, s, factor);
    const batches = isExtraction(job) ? Math.floor(amount * perHour) : amount;
    const minutes = isExtraction(job) ? amount * 60 : Math.max(1, Math.ceil((amount / perHour) * 60));
    return { batches, minutes, output: batches * job.output.qty, xp: Math.round((s.productionXpPerHour * minutes) / 60) };
}

/** The most batches one processing booking can hold: what fits in the longest job, and what the inputs allow. */
export function maxBatches(job: ProductionJob, inventory: InventoryItem[] | undefined, tier: number, level: number, s: ProductionSettings = getProductionSettings(), factor = 1): number {
    const byTime = Math.floor(s.productionMaxHours * batchesPerHourFor(job, tier, level, s, factor));
    const byInputs = job.inputs.reduce((min, x) => Math.min(min, Math.floor(materialCount(inventory, x.material) / x.qty)), Number.MAX_SAFE_INTEGER);
    return Math.max(0, Math.min(byTime, byInputs));
}
